"use client"

import { useState, useEffect } from 'react'
import { useApi } from '@/lib/api'
import { useCodeValues } from '@/hooks/use-code-values'
import { useFormatters } from '@/hooks/use-formatters'
import { saveAs } from 'file-saver'
import { normalizeUTCDateOnlyToLocal } from '@/lib/utils'

// BillingTransaction data interface - Pure API model
export interface BillingTransaction {
  EntityId: number                    // Entity ID
  BillingTransactionNumber: string    // Billing transaction reference number
  TransactionTypeId: number           // Transaction type ID (needs code lookup)
  TotalAmount: number                 // Total amount
  CurrencyId: number                  // Currency ID (will show code instead of text)
  BillingTransactionStatusId: number  // Status ID (needs code lookup)
  BillingDate: string | null          // ISO date string (nullable)
  InvoiceNumber: string               // Invoice reference number
  InvoiceStatusId: number             // Invoice status ID (needs code lookup)
  TransactionHeaderId: number         // Transaction ID for linking (selected but not displayed)
  InvoiceNote1: string | null         // Invoice note field
}

// Type-safe field selection based on BillingTransaction model
type BillingTransactionField = keyof BillingTransaction

// Default fields to select for billing transaction reports
const DEFAULT_BILLING_TRANSACTION_FIELDS: BillingTransactionField[] = [
  'EntityId',
  'BillingTransactionNumber',
  'TransactionTypeId',
  'TotalAmount',
  'CurrencyId',
  'BillingTransactionStatusId',
  'BillingDate',
  'InvoiceNumber',
  'InvoiceStatusId',
  'TransactionHeaderId',
  'InvoiceNote1'
]

// Reports API types based on the Swagger specification
export interface ReportSearchParams {
  filter?: string
  orderBy?: string
  top?: number
  skip?: number
  search?: string
  facets?: string[]
  select?: string
}

export interface ReportResponse<T> {
  value: T[]
  '@odata.count'?: number
  '@odata.nextLink'?: string
  facets?: Record<string, any>
}

const BILLING_TRANSACTION_REPORT_TYPE_ID = 20
const MAX_RECORDS_PER_REQUEST = 1000

export interface UseBillingTransactionReportsOptions {
  engagementId?: number
  autoFetch?: boolean
  searchParams?: ReportSearchParams
  currentPage?: number
  pageSize?: number
}

export interface UseBillingTransactionReportsResult {
  billingTransactions: BillingTransaction[]
  loading: boolean
  error: string | null
  totalCount: number
  isExporting: boolean
  exportError: string | null
  refetch: () => Promise<void>
  exportToCSV: (searchParams?: ReportSearchParams) => Promise<void>
}

export function useBillingTransactionReports(options: UseBillingTransactionReportsOptions = {}): UseBillingTransactionReportsResult {
  const { engagementId, autoFetch = true, searchParams = {}, currentPage = 1, pageSize = 10 } = options
  const { reportsApi } = useApi()
  const { getCodeValueText, getCodeValues } = useCodeValues()
  const { formatCurrency, formatDate } = useFormatters()

  // Helper function to get currency code instead of text
  const getCurrencyCode = (currencyId: number): string => {
    const currencies = getCodeValues('geo.CodeCurrency', false)
    const currency = currencies.find(c => c.id === currencyId)
    return currency?.code || currencyId.toString()
  }
  
  const [billingTransactions, setBillingTransactions] = useState<BillingTransaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const buildQueryParams = (params: ReportSearchParams): URLSearchParams => {
    const queryParams = new URLSearchParams()
    
    queryParams.append('$select', params.select ?? DEFAULT_BILLING_TRANSACTION_FIELDS.join(','))
    
    if (params.filter) queryParams.append('$filter', params.filter)
    if (params.orderBy) queryParams.append('$orderby', params.orderBy)
    if (params.top) queryParams.append('$top', params.top.toString())
    if (params.skip) queryParams.append('$skip', params.skip.toString())
    if (params.search) queryParams.append('$search', params.search)
    if (params.facets?.length) {
      params.facets.forEach(facet => queryParams.append('facets', facet))
    }
    
    return queryParams
  }

  const buildFilterWithEngagement = (baseFilter: string = ''): string => {
    let filter = baseFilter
    if (engagementId) {
      const engagementFilter = `EngagementId eq ${engagementId}`
      filter = filter ? `${filter} and ${engagementFilter}` : engagementFilter
    }
    return filter
  }

  const makeReportApiCall = async (params: ReportSearchParams): Promise<ReportResponse<BillingTransaction>> => {
    if (!reportsApi) {
      throw new Error('Reports API not available')
    }

    const queryParams = buildQueryParams(params)
    const path = `Report/${BILLING_TRANSACTION_REPORT_TYPE_ID}?${queryParams.toString()}`
    return await reportsApi.get<ReportResponse<BillingTransaction>>(path)
  }

  const fetchBillingTransactions = async (params: ReportSearchParams = searchParams): Promise<void> => {
    try {
      setLoading(true)
      setError(null)

      // Build filter with engagement
      const filter = buildFilterWithEngagement(params.filter)

      // Calculate skip based on pagination
      const skip = (currentPage - 1) * pageSize
      const finalParams = { ...params, filter, top: pageSize, skip }

      const response = await makeReportApiCall(finalParams)

      // Transform date strings from UTC format to local format
      const transformedBillingTransactions = (response.value || []).map(transaction => ({
        ...transaction,
        BillingDate: normalizeUTCDateOnlyToLocal(transaction.BillingDate)
      }))

      setBillingTransactions(transformedBillingTransactions)
      setTotalCount(response['@odata.count'] || response.value?.length || 0)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch billing transactions'
      setError(errorMessage)
      console.error('Billing transaction reports fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchAllBillingTransactions = async (params: ReportSearchParams = {}): Promise<BillingTransaction[]> => {
    const allBillingTransactions: BillingTransaction[] = []
    let skip = 0
    let hasMoreData = true

    // Build filter with engagement
    const filter = buildFilterWithEngagement(params.filter)
    const finalParams = { ...params, filter }

    while (hasMoreData) {
      const batchParams = { ...finalParams, top: MAX_RECORDS_PER_REQUEST, skip }
      const response = await makeReportApiCall(batchParams)
      const batchBillingTransactions = response.value || []
      
      // Transform date strings from UTC format to local format
      const transformedBatch = batchBillingTransactions.map(transaction => ({
        ...transaction,
        BillingDate: normalizeUTCDateOnlyToLocal(transaction.BillingDate)
      }))
      
      allBillingTransactions.push(...transformedBatch)
      
      // Check if we have more data
      hasMoreData = batchBillingTransactions.length === MAX_RECORDS_PER_REQUEST
      skip += MAX_RECORDS_PER_REQUEST
    }

    return allBillingTransactions
  }

  const refetch = async (): Promise<void> => {
    await fetchBillingTransactions(searchParams)
  }

  const transformBillingTransactionToCSVRow = (transaction: BillingTransaction): string[] => {
    const currencyCode = getCurrencyCode(transaction.CurrencyId)
    return [
      `"${transaction.EntityId}"`,
      `"${transaction.BillingTransactionNumber}"`,
      `"${getCodeValueText(transaction.BillingTransactionStatusId, 'trn.CodeBillingTransactionStatus')}"`,
      `"${getCodeValueText(transaction.TransactionTypeId, 'trn.CodeTransactionType')}"`,
      `"${currencyCode} ${formatCurrency(transaction.TotalAmount, currencyCode)}"`,
      `"${formatDate(transaction.BillingDate, '-')}"`,
      `"${transaction.InvoiceNumber || ''}"`,
      `"${getCodeValueText(transaction.InvoiceStatusId, 'inv.CodeInvoiceStatus')}"`,
      `"${transaction.InvoiceNote1 || ''}"`,
    ]
  }

  const generateCSV = (billingTransactions: BillingTransaction[]): string => {
    const headers = [
      'ID',
      'Transaction #',
      'Status',
      'Type',
      'Total Amount',
      'Billing Date',
      'Invoice Number',
      'Invoice Status',
      'Note'
    ]

    const csvRows = [
      headers.join(','), // Header row
      ...billingTransactions.map(transaction => transformBillingTransactionToCSVRow(transaction).join(','))
    ]

    return csvRows.join('\n')
  }

  const generateFileName = (): string => {
    const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD format
    const engagementPart = engagementId ? `engagement-${engagementId}-` : ''
    return `${engagementPart}billing-transactions-${today}.csv`
  }

  const exportToCSV = async (exportParams: ReportSearchParams = searchParams): Promise<void> => {
    try {
      setIsExporting(true)
      setExportError(null)

      // Fetch all billing transactions
      const allBillingTransactions = await fetchAllBillingTransactions(exportParams)
      
      if (allBillingTransactions.length === 0) {
        throw new Error('No billing transactions found to export')
      }

      // Generate CSV content
      const csvContent = generateCSV(allBillingTransactions)
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const fileName = generateFileName()
      
      saveAs(blob, fileName)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export billing transactions'
      setExportError(errorMessage)
      console.error('Billing transaction export error:', err)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    if (autoFetch) {
      fetchBillingTransactions(searchParams)
    }
  }, [engagementId, autoFetch, currentPage, pageSize, searchParams]) // Re-fetch when engagement, page, page size, or search params change

  return {
    billingTransactions,
    loading,
    error,
    totalCount,
    isExporting,
    exportError,
    refetch,
    exportToCSV
  }
} 